import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReplaySubject, catchError, map, of, switchMap, timer } from 'rxjs';
import { LaneCustomer, LaneIdentity, LaneLocationRole, LaneSite, LaneSitesResponse } from './lane.models';
import { LaneRoutingService } from './lane.service';
interface LocationRequest {
    date: string;
    customer: string | null;
    role: LaneLocationRole | null;
    query: string;
    mode: 'known' | 'all';
    offset: number;
    delayed: boolean;
    pickup: LaneIdentity['shipper'] | null;
    consignee: LaneIdentity['consignee'] | null;
}
@Component({
    selector: 'app-lane-picker', standalone: true, imports: [CommonModule, FormsModule],
    styleUrls: ['./lane-picker.component.scss'],
    templateUrl: './lane-picker.component.html'
})
export class LanePickerComponent implements OnInit, OnChanges {
    @Input()
    label = 'Location';
    @Input()
    customer = false;
    @Input()
    disabled = false;
    @Input()
    value: LaneCustomer | LaneSite | null = null;
    @Input()
    customerContactUuid: string | null = null;
    @Input()
    locationRole: LaneLocationRole | null = null;
    @Input()
    effectiveDate = '';
    @Input()
    pickup: LaneIdentity['shipper'] | null = null;
    @Input()
    consignee: LaneIdentity['consignee'] | null = null;
    @Output()
    valueChange = new EventEmitter<LaneCustomer | LaneSite | null>();
    private readonly destroyRef = inject(DestroyRef);
    private readonly requests = new ReplaySubject<LocationRequest>(1);
    private allowAutoSelect = true;
    private requested = false;
    query = '';
    busy = false;
    error = '';
    offset = 0;
    mode: 'known' | 'all' = 'known';
    results: (LaneCustomer | LaneSite)[] = [];
    meta: LaneSitesResponse['meta'];
    constructor(private readonly api: LaneRoutingService) { }
    get siteValue(): LaneSite | null { return this.value ? this.asSite(this.value) : null; }
    get known(): boolean { return !!this.meta && this.meta.source !== 'all_locations'; }
    get sourceDescription(): string {
        if (this.meta?.source === 'routing_stops')
            return 'Configured customer routing stops';
        if (this.meta?.source === 'pool_stores')
            return 'Customer stores from Pool Manager';
        if (this.meta?.source === 'shipping_origins')
            return 'Saved customer shipping origins';
        return 'Customer pools and distribution centers';
    }
    asSite(value: LaneCustomer | LaneSite): LaneSite | null { return 'address_uuid' in value ? value : null; }
    ngOnChanges(changes: SimpleChanges): void {
        if (changes['customerContactUuid'] || changes['locationRole'] || changes['pickup'] || changes['consignee'] || changes['effectiveDate']) {
            this.query = '';
            this.offset = 0;
            this.mode = 'known';
            this.meta = undefined;
            this.allowAutoSelect = true;
            this.load();
        }
    }
    ngOnInit(): void {
        this.requests.pipe(switchMap(request => {
            if (request.role && !request.customer)
                return of({ request, response: null, error: '' });
            return (request.delayed ? timer(250) : of(0)).pipe(switchMap(() => this.customer
                ? this.api.customers(request.query).pipe(map(result => ({ data: result.data } as {
                    data: (LaneCustomer | LaneSite)[];
                    meta?: LaneSitesResponse['meta'];
                })))
                : this.api.sites(request.query, request.customer || undefined, request.role || undefined, request.mode, request.offset, request.pickup, request.consignee, request.date)), map(response => ({ request, response, error: '' })), catchError(error => of({ request, response: null, error: error.error?.message || 'Location lookup failed.' })));
        }), takeUntilDestroyed(this.destroyRef)).subscribe(({ request, response, error }) => {
            this.busy = false;
            this.error = error;
            this.results = response?.data || [];
            this.meta = response?.meta;
            if (this.allowAutoSelect && !this.value && !this.disabled && !request.query && !request.offset
                && this.meta?.source === 'shipping_origins' && this.meta.total === 1 && !this.meta.unavailable) {
                this.allowAutoSelect = false;
                this.choose(this.results[0]);
            }
        });
        if (!this.requested)
            this.load();
    }
    load(delayed = false): void {
        this.requested = true;
        this.results = [];
        this.error = '';
        this.busy = true;
        this.requests.next({ date: this.effectiveDate, customer: this.customerContactUuid, role: this.locationRole, query: this.query, mode: this.mode, offset: this.offset, delayed, pickup: this.pickup, consignee: this.consignee });
    }
    search(): void { this.allowAutoSelect = false; this.offset = 0; this.load(true); }
    page(delta: number): void { this.offset = Math.max(0, this.offset + delta); this.load(); }
    setMode(mode: 'known' | 'all'): void { this.allowAutoSelect = false; this.mode = mode; this.query = ''; this.offset = 0; this.load(); }
    chooseKnown(index: string): void { if (index !== '') {
        const value = this.results[Number(index)];
        if (value)
            this.choose(value);
    } }
    choose(value: LaneCustomer | LaneSite): void {
        if (this.disabled || this.asSite(value)?.valid === false)
            return;
        this.valueChange.emit(value);
    }
    clear(): void { this.allowAutoSelect = false; this.valueChange.emit(null); this.query = ''; this.offset = 0; this.load(); }
    optionLabel(value: LaneCustomer | LaneSite): string {
        const site = this.asSite(value);
        if (!site)
            return value.display_name;
        const parts = [site.store_number, site.display_name, [site.city, site.region].filter(Boolean).join(' '), site.source_label];
        if (site.valid === false)
            parts.push('Needs review: ' + (site.issues?.[0] || 'Location unavailable'));
        return parts.filter(Boolean).join(' · ');
    }
}
